<!-- src/apps/secret/reveal/branded/ShowSecret.vue -->

<script setup lang="ts">
  /**
   * Branded secret display implementation that maintains consistent UI between confirmation
   * and reveal states by leveraging BaseSecretDisplay for both.
   *
   * This component handles secrets for custom domains, ensuring brand consistency by:
   * 1. Using identical layouts for both confirmation and reveal states
   * 2. Applying domain-specific styling (colors, fonts, corner styles)
   * 3. Displaying branded logos when available
   *
   * @see SecretConfirmationForm - Handles passphrase entry using BaseSecretDisplay
   * @see SecretDisplayCase - Displays revealed content using BaseSecretDisplay
   */

  import BaseShowSecret from '@/shared/components/base/BaseShowSecret.vue';
  import FooterAttribution from '@/apps/secret/components/layout/SecretFooterAttribution.vue';
  import SecretConfirmationForm from '@/apps/secret/components/branded/SecretConfirmationForm.vue';
  import SecretDisplayCase from '@/apps/secret/components/branded/SecretDisplayCase.vue';
  import { useProductIdentity } from '@/shared/stores/identityStore';

  import UnknownSecret from './UnknownSecret.vue';

  interface Props {
    secretIdentifier: string;
    domainId: string;
    displayDomain: string;
    siteHost: string;
  }

  const productIdentity = useProductIdentity();
  const brandSettings = productIdentity.brand; // Not reactive

  defineProps<Props>();
</script>

<template>
  <BaseShowSecret
    :secret-identifier="secretIdentifier"
    :branded="true"
    :site-host="siteHost"
    class="container mx-auto mt-24 px-4">
    <!--
      No #loading slot: BaseShowSecret renders its own <SecretSkeleton> for the
      secret fetch (see BaseShowSecret template). A #loading slot here never
      rendered (the base has no <slot name="loading">), so the old spinner was
      dead markup.
    -->

    <!-- Error slot -->
    <template #error="{ error }">
      <div class="w-full max-w-xl rounded-lg bg-red-50 p-4 text-red-700">
        {{ error }}
      </div>
    </template>

    <!-- Confirmation slot -->
    <template #confirmation="{ record, details, error, isLoading, onConfirm }">
      <div
        :class="{
          'rounded-lg': brandSettings?.corner_style === 'rounded',
          'rounded-2xl': brandSettings?.corner_style === 'pill',
          'rounded-none': brandSettings?.corner_style === 'square',
          'mx-auto max-w-2xl space-y-20': true,
        }">
        <SecretConfirmationForm
          :secret-identifier="secretIdentifier"
          :record="record"
          :details="details"
          :domain-id="domainId"
          :error="error"
          :is-submitting="isLoading"
          @user-confirmed="onConfirm" />
      </div>
    </template>

    <!-- Reveal slot -->
    <template #reveal="{ record, details }">
      <div class="mx-auto w-full max-w-2xl">
        <SecretDisplayCase
          aria-labelledby="secret-heading"
          :secret-identifier="secretIdentifier"
          :record="record"
          :details="details"
          :domain-id="domainId"
          class="w-full" />
      </div>
    </template>

    <!-- Unknown secret slot -->
    <template #unknown="{}">
      <div class="mx-auto max-w-2xl">
        <UnknownSecret
          :branded="true"
          :brand-settings="brandSettings ?? undefined" />
      </div>
    </template>

    <!-- Footer slot -->
    <template #footer="{ }">
      <div class="flex flex-col items-center space-y-8 py-8">
        <FooterAttribution
          :site-host="siteHost"
          :show-nav="true"
          :show-terms="true" />
      </div>
    </template>
  </BaseShowSecret>
</template>

<style scoped>
  .logo-container {
  transition: all 0.3s ease;
}

.logo-container img {
  max-width: 100%;
  height: auto;
}

:focus {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
